import { useMemo, useState } from 'react';

import { toSortModel, toSortParams } from '~/utils/sort-params';

/**
 * Server-side column sorting for a DataGrid.
 *
 * `onChange(sortParams)` is called with the new `{ sortBy, sortOrder }` (or `{}` when cleared)
 * whenever the user changes the sort, so the page can refetch from page 1 with it passed explicitly.
 *
 * `currentParams` makes the hook controlled: when the sort lives outside the component (page state,
 * Redux, or the URL) pass it here and the grid always shows that sort, including after back/forward
 * navigation. Without it the hook keeps the sort in local state.
 *
 * Usage:
 *   const { sortParams, sortProps } = useSortParams(columns, refetch);
 *   <DataGrid columns={columns} {...sortProps} />
 */
const useSortParams = (columns, onChange, currentParams) => {
  const [localModel, setLocalModel] = useState([]);
  const isControlled = currentParams !== undefined;

  // Memoised on the values so the grid receives a stable model while the sort is unchanged.
  const controlledModel = useMemo(
    () => toSortModel(currentParams, columns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentParams?.sortBy, currentParams?.sortOrder]
  );
  const sortModel = isControlled ? controlledModel : localModel;

  const handleSortModelChange = (nextModel) => {
    if (!isControlled) setLocalModel(nextModel);
    onChange?.(toSortParams(nextModel, columns));
  };

  return {
    sortParams: toSortParams(sortModel, columns),
    // Spread onto the DataGrid: sorting is done by the API, one column at a time.
    sortProps: {
      sortingMode: 'server',
      disableMultipleColumnsSorting: true,
      sortModel,
      onSortModelChange: handleSortModelChange,
    },
  };
};

export default useSortParams;
